import { BugoBrandBrief } from '@shared/api/bugoBrand/bugoBrand.interface';

// CS 응대 메모 (GiftCsMemo) — append-only, 스태프(Admin·GiftAgent) 전용.
// 스펙: docs/gift-cs-memo-spec.md
//
// 주의 1) GiftCoupon.memo(구매자가 수령자에 대해 남기는 메모)와 전혀 다른 것.
//        이쪽은 "어떤 문의를 받아 어떻게 처리했는지" 응대 기록이다.
// 주의 2) 구매자·수령자·상조 관리자에게는 백엔드가 populate에서 강제로 제거한다.
//        유저용 화면에서는 절대 렌더하지 말 것.

export type GiftCsMemoByRole = 'Admin' | 'GiftAgent';

export interface GiftCsMemo {
  _id: string;
  /** 쿠폰/배송 단위 메모에도 항상 스탬프된다 (주문 축 통합 조회 키) */
  giftOrder?: string;
  /** 쿠폰 단위 메모일 때만 */
  giftCoupon?: string;
  /** 배송 단위 메모일 때만 */
  giftDelivery?: string;

  by: string;
  /** 작성자 이름 스냅샷 — populate 없이 바로 렌더 */
  byName: string;
  byRole: GiftCsMemoByRole;

  /** true = Admin 전용 (GiftAgent 비노출). Admin만 세팅 가능 */
  adminOnly?: boolean;

  body: string;
  // axios 응답 인터셉터가 ISO 문자열을 Date로 바꿔 내려준다 (docs 메모리: axios 날짜 자동 변환)
  createdAt: Date;
  updatedAt?: Date;
}

/** 셋 중 정확히 하나만 채운다 */
export interface GiftCsMemoTarget {
  giftOrder?: string;
  giftCoupon?: string;
  giftDelivery?: string;
}

export interface GiftCsMemoCreateDto extends GiftCsMemoTarget {
  body: string;
  adminOnly?: boolean;
}

export type GiftCsMemoAxis = 'giftOrder' | 'giftCoupon' | 'giftDelivery';

// ─── CS 응대 로그 (전체 목록 페이지) ───
// 메모 하나가 어떤 건이었는지 표에서 바로 읽히도록 백엔드가 대상 요약을 붙여준다.
// 스펙: docs/gift-cs-memo-spec.md §3.5

export type GiftCsMemoLogTargetFilter = 'all' | 'order' | 'coupon' | 'delivery';

/**
 * 확장 필드 계약: docs/gift-cs-memo-log-improvement-checklist.md §2-B(D안) · §2-C.
 * 아직 서버가 안 내려주는 값이 섞여 있고 전부 optional이다 —
 * 화면은 값이 하나라도 있을 때만 해당 열을 띄우므로(조건부 렌더), 서버가 붙이면
 * 프론트 배포 없이 열이 살아난다.
 */
export interface GiftCsMemoLogItem extends GiftCsMemo {
  /** 주문 요약 — 메모 대상이 무엇이든 항상 (주문 미연결 레거시만 없음) */
  order?: {
    _id: string;
    receiverName?: string;
    receiverPhoneNumber?: string;
    purchasedAt?: Date;
    /**
     * 상조(부고브랜드) 표기명 — `getAgencyBrandName`과 같은 규칙(nickName 우선,
     * 멀티 브랜드는 fevent.bugoBrandAgencyIndex 항목)으로 서버가 확정해 내린다.
     * "웅진프리드라이프 건인지" 를 표에서 바로 가르기 위한 값.
     * 요청: docs/shop-order-cs-memo-log-enrich-spec.md §13 (REQ-7) — 구서버는 undefined.
     */
    bugoBrandName?: string;
    /**
     * 이름을 확정하지 않고 `order.bugoBrand` ref를 **populate만** 해준 경우.
     * 둘 중 뭐가 오든 화면은 `orderBrandName()`으로 같은 값을 만든다.
     * (멀티 브랜드 상조는 bugoBrandAgencyIndex가 있어야 정확 — 없으면 bugoAgency로 폴백)
     */
    bugoBrand?: BugoBrandBrief | string;
    bugoBrandAgencyIndex?: number;
    /**
     * 이 주문에 담긴 상품 유형들 (`orderDetails[].type` 의 distinct).
     * 한 주문에 쿠폰형·배송형이 섞일 수 있어 단일값이 아니라 **배열**이다 —
     * length > 1 이면 `혼합`.
     *
     * ⚠️ 프론트가 메모 축(giftCoupon/giftDelivery 유무)으로 추론하면 안 된다.
     * 혼합 주문의 쿠폰 하나에 달린 메모를 "쿠폰형 주문"으로 잘못 말하게 되고,
     * 그게 정확히 이 필드를 만드는 이유인 케이스다.
     */
    itemTypes?: ('Coupon' | 'Delivery')[];
    /**
     * **주문 축에 달린** 메모 수 — `giftCoupon`·`giftDelivery` 가 둘 다 없는 메모만.
     * 기간·페이지 무관, 소프트 삭제 제외, GiftAgent 응답은 adminOnly 제외.
     *
     * 서버의 판정 기준과 프론트의 통합 타임라인 필터 기준이 같아야 배지 숫자와 모달 건수가 맞는다
     * (프론트도 `!m.giftCoupon && !m.giftDelivery` 로 거른다 — 백엔드 가이드 §5-4 회신).
     */
    memoCount?: number;
  };
  /** 쿠폰 단위 메모일 때만 */
  coupon?: {
    _id: string;
    code?: string;
    seq?: number;
    fullCode?: string;
    /** 수령자가 고른 상품 */
    itemName?: string;
    redeemStatus?: string;
    /**
     * **관리자가 기프티콘을 발송한** 시각 (`redeemStatus: 'Delivered'` 의 짝).
     * ⚠️ 이력이 아니라 **마지막 발송 시각**이다 — 발송완료 취소(undeliver) 시 gifticon이
     * 통째로 지워져 이 값도 사라지고, 재발송하면 새 시각이 박힌다.
     * "전에 보냈다 취소된 적 있나"는 undeliverCount로 읽을 것.
     */
    sentAt?: Date;
    /** 발송완료 취소 횟수 (0이면 이력 없음) */
    undeliverCount?: number;
    /**
     * 'NotShared' | 'Shared' | 'Viewed'.
     * ⚠️ **"보냈는데 못 받았대요"의 답은 sentAt이 아니라 이쪽에 있다** —
     * 공유는 됐는데 수령자가 교환신청을 안 했거나, 신청은 했는데 발송 대기 중인 상태.
     */
    shareStatus?: string;
    /** 구매자가 수령자에게 쿠폰 링크를 공유한 시각 (≠ 관리자 발송) */
    sharedAt?: Date;
    /** 수령자가 처음 열어본 시각 */
    viewedAt?: Date;
    /**
     * 발송 매체 — 'Addcon' | 'Officecon' | 'GiftiShow' | 'EumGift'.
     * 매체마다 발신번호·알림 종류가 달라서 "모르는 번호로 문자 왔는데 이거 맞냐"에 답하는 값.
     */
    deliveryChannel?: string;
    /**
     * **교환신청자 = 실제 수령자** (`coupon.recipient.name`).
     * order.receiverName(상주·주문자)과 **다른 사람**이다 — 쿠폰은 구매자가 공유하고
     * 받은 사람이 자기 이름·번호로 신청하므로, "누가 받았냐" 응대의 답이 여기 있다.
     * 미신청(NotShared/Shared)이면 없음.
     *
     * 백엔드 구현 완료(REQ-6, 스펙 §10~§12)·배포 대기 — 구서버에서는 undefined다.
     */
    recipientName?: string;
    recipientPhoneNumber?: string;
    /**
     * **전달자(구매자)가 받는 사람을 가리키려고 남긴 메모** (`coupon.memo`).
     * 수령자가 교환신청 전이면 이름이 아예 없어서, 표에서는 이 메모가 사실상 이름 자리다
     * ("김부장님", "이모"처럼). 신청 후에도 구매자 기준 호칭이라 같이 본다.
     */
    memo?: string;
    /** 번호 인증 기록 (cert 성공 시 저장) — "본인이 신청한 게 맞냐"의 근거 */
    verification?: { phoneNumber?: string; verifiedAt?: Date };
    /** 수령자 서명 이미지 URL (`recipient.signatureImage.url` — populate 필요) */
    signatureImageUrl?: string;
    /**
     * 수령자가 **교환신청**한 시각 (`coupon.recipient.requestedAt`).
     * ⚠️ 한 건에서 셋이 다 다르다: sharedAt(구매자 공유) → requestedAt(수령자 신청)
     * → sentAt(관리자 발송). 화면 라벨을 섞어 쓰면 응대가 틀어진다 (스펙 §11).
     */
    requestedAt?: Date;
    /** 이 쿠폰 건에 달린 메모 수 — 기간·페이지 무관 */
    memoCount?: number;
  };
  /** 배송 단위 메모일 때만 */
  delivery?: {
    _id: string;
    code?: string;
    deliveryStatus?: string;
    /** 배송 상품명 — 쿠폰 축의 coupon.itemName 과 같은 자리 */
    itemName?: string;
    /**
     * **실제 수령자** — order.receiverName(상주·주문자 측)과 다르다.
     * 개별배송(Individual)은 건마다 수령자가 다르므로 배송 행에는 이 값을 써야 한다.
     */
    recipientName?: string;
    /** 수령자 연락처 — 쿠폰 축과 같은 자리 (REQ-6, 구서버는 undefined) */
    recipientPhoneNumber?: string;
    /**
     * 수령자가 **배송지를 제출**한 시각 — 쿠폰의 requestedAt과 짝인 자리 (역제안 P).
     * 일괄배송(Bulk)은 구매자가 주문 시 배송지를 넣으므로 **값이 없는 게 정상**이다.
     */
    submittedAt?: Date;
    /** 택배 발송 시각 */
    shippedAt?: Date;
    /** 택배 **도착** 시각. ⚠️ 쿠폰의 sentAt(발송)과 의미가 반대라 라벨을 다르게 쓸 것 */
    deliveredAt?: Date;
    /** 배송 예정일 */
    estimatedDeliveryDate?: Date;
    /** 표에는 넣지 않고 모달·엑셀에만 */
    trackingNumber?: string;
    carrier?: string;
    /** 이 배송 건에 달린 메모 수 — 기간·페이지 무관 */
    memoCount?: number;
  };
}

export interface GiftCsMemoLogParams {
  /** YYYY-MM-DD (KST 하루 경계, createdAt 기준) */
  from?: string;
  to?: string;
  target?: GiftCsMemoLogTargetFilter;
  /** 본문·작성자명·쿠폰코드·주문자명 부분일치 */
  q?: string;
  /** Admin 전용 메모만 보기 (Admin만 의미 있음) */
  adminOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface GiftCsMemoLogResponse {
  items: GiftCsMemoLogItem[];
  total: number;
  page: number;
  limit: number;
}

/** 타깃 객체에서 실제로 채워진 축과 id를 뽑는다 (없으면 null) */
export const resolveCsMemoTarget = (
  target: GiftCsMemoTarget,
): { axis: GiftCsMemoAxis; id: string } | null => {
  if (target.giftCoupon) return { axis: 'giftCoupon', id: target.giftCoupon };
  if (target.giftDelivery) return { axis: 'giftDelivery', id: target.giftDelivery };
  if (target.giftOrder) return { axis: 'giftOrder', id: target.giftOrder };
  return null;
};
